import React from 'react';
import clsx from 'clsx';
import Collapsible from '@dashboard/components/Collapsible';
import { DragIcon } from '@dashboard/icons/DragIcon';
import Button, { getButtonClass } from '@dashboard/components/Button';
import Tooltip from '@dashboard/components/Tooltip';
import Lozenge from '@dashboard/components/Lozenge';
import { ChevronDownIcon, ChevronUpIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useVariantHelpers } from '../useVariantHelpers';
import VariantEdit from './VariantEdit';
import { useVariantEdit } from '../VariantEditContext';

export default function VariantItem({ variant, dragHandleProps }) {
  const { isAddingVariant, setIsEditingVariant } = useVariantEdit();

  const { onEnableDisableVariant, onDuplicateVariant, onDeleteVariant, onCancelAddVariant } =
    useVariantHelpers(variant._id || variant.key);

  const enabledOptionsCount = variant.options.filter(option => option.status === 'enabled').length;

  return (
    <div className="w-full">
      <Collapsible.Root
        open={variant.isEditing}
        onOpenChange={open => {
          if (variant.isNew) return;

          setIsEditingVariant(open, variant._id);
        }}
      >
        <div
          className={clsx(
            'flex flex-row gap-2 items-center justify-start sticky top-0 z-10 rounded-t',
          )}
        >
          <div {...dragHandleProps}>
            <DragIcon className="w-4 h-4" />
          </div>

          <Collapsible.Trigger
            as="button"
            className={clsx(
              `py-3 truncate w-full text-left flex items-center justify-between !ring-0 !focus:ring-0`,
              getButtonClass('bare'),
              {
                '!text-gray-900 !hover:text-gray-900 cursor-not-allowed': isAddingVariant, // remove hover effect when adding variant
              },
            )}
            disabled={variant.isNew}
            type="button"
          >
            {!variant.isNew && (
              <>
                <span
                  className={clsx('font-medium truncate', {
                    'opacity-50': variant.status === 'disabled',
                  })}
                >
                  {variant.name}
                  <span>{variant.required ? ' *' : ''}</span>
                  {variant.hidden && <Lozenge className="ml-2">Hidden</Lozenge>}
                  {variant.showHideLogic?.enabled && (
                    <Lozenge className="ml-2">Conditional</Lozenge>
                  )}
                </span>
              </>
            )}

            <div className="flex gap-x-2 items-center ml-auto">
              {!variant.isNew && (
                <>
                  {variant.status === 'disabled' && (
                    <Tooltip label="Disabled variants will not be shown to customers">
                      <Lozenge appearance="default" className="ml-2">
                        Disabled
                      </Lozenge>
                    </Tooltip>
                  )}

                  <span className="text-xs text-gray-500">
                    {enabledOptionsCount} option
                    {enabledOptionsCount === 1 ? '' : 's'}
                  </span>
                </>
              )}

              <span className="ml-auto">
                {variant.isEditing ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </span>
            </div>
          </Collapsible.Trigger>

          <div className="flex ml-auto gap-2 items-center">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild disabled={isAddingVariant}>
                <Button
                  aria-label="more options"
                  appearance="icon-button"
                  size="narrow"
                  disabled={isAddingVariant}
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="origin-top-right absolute right-0 z-20 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-100 focus:outline-none overflow-hidden"
                  side="bottom"
                  align="end"
                >
                  <DropdownMenu.Item
                    onClick={onDuplicateVariant}
                    className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                  >
                    Duplicate
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onClick={onEnableDisableVariant}
                    className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                  >
                    {variant.status === 'enabled' ? 'Disable' : 'Enable'}
                  </DropdownMenu.Item>

                  <DropdownMenu.Item
                    onClick={onDeleteVariant}
                    className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                  >
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>

        <Collapsible.Content
          className={clsx(`pb-4 pt-2 relative`, {
            'opacity-50 pointer-events-none': variant.status === 'disabled',
          })}
        >
          <VariantEdit
            variant={variant}
            cancel={() => {
              if (variant.isNew) {
                onCancelAddVariant();
              } else {
                setIsEditingVariant(false, variant._id);
              }
            }}
          />
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}
